import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import moment from 'moment';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private analyticsEndPoint = 'dashboard/ujmanalytics/events/';
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  private extractData(res: Response) {
    const body = res;
    return body || {};
  }

  /**
   * Schedule the selected journey's data to be refreshed
   */
  scheduleAnalyticsRefresh(appId, journeyId) {
    const url = '/dashboard/ujmanalytics/events/refresh?jid=' + journeyId;
    return this.http.get<any>(url).pipe(
      map((res: Response) => {
        const body = res;
        return body || {};
      })
    );
  }

  getEventDetails(analytics_id, journey_id, normalization, branch_ids, step, start, stop): Observable<any> {
    const endPoint = 'dashboard/ujmanalytics/smt/events/query';
    const data = {
      'analytics_id': analytics_id,
      'journey_id': journey_id,
      'normalization': normalization,
      'branch_ids': branch_ids,
      'step': step,
      'start': start,
      'stop': stop
    };
    const dataToSend = {
      'params': data
    };
    return this.http.post<any>(endPoint, data, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  getPromptsAnalytics(journeyId, nodePath, step, start, stop, lidx): Observable<any> {
    const endPoint = 'dashboard/ujmanalytics/smt/prompts/query';
    const data = {
      'journey_id': journeyId,
      'node_path': nodePath,
      'step': step,
      'start': start,
      'stop': stop,
      'lidx': lidx
    };
    const dataToSend = { // not being used (?)
      'params': data
    };
    return this.http.post<any>(endPoint, data, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  getEventTotalCount(analytics_id, journey_id, branch_ids): Observable<any> {
    const endPoint = 'dashboard/ujmanalytics/smt/events/totals';
    const data = {
      'analytics_id': analytics_id,
      'journey_id': journey_id,
      'branch_ids': branch_ids,
    };
    const dataToSend = {
      'params': data
    };
    return this.http.post<any>(endPoint, data, this.httpOptions).pipe(
      map(this.extractData)
    );
  }

  /**
   * dilDataSendmail: API wrapper for createDILdownloadRequest,
   * used to send mail to emailIDs with data.
   *
   *  INPUT FORMAT:
   *  @requestData : {
   *    app_id: appId,
   *    email_ids: [emailIds],
   *    journey_hash: journeyHash,
   *    prompt: promptObj,
   *    prompt_template: template name,
   *    duration: duration of data fetch from today.
   *  }
   */
  dilDataSendmail(requestData) {
    if (!this.validateDILDownloadRequest(requestData)) {
      return;
    }
    return this.createDILdownloadRequest(requestData);
  }

  private validateDILDownloadRequest(requestData) {
    if (!requestData.prompt || !requestData.prompt.template) {
      return false;
    }
    switch (requestData.prompt.template) {
      case 'newsletter_subscription':
      case 'beta_test_signups':
      case 'rating_with_user_feedback':
      case 'nps_with_user_feedback':
      case 'user_feedback':
        return true;
      default:
        return false;
    }
  }

  private getDILDownloadDataFields(promptType) {
    switch (promptType) {
      case 'newsletter_subscription':
      case 'beta_test_signups':
        return [{
          'header': 'Email',
          'field': 'prompt_input1'
        }];
      case 'user_feedback':
        return [{
          'header': 'Feedback',
          'field': 'prompt_input1'
        }];
      case 'rating_with_user_feedback':
        return [{
          'header': 'Feedback',
          'field': 'prompt_input1'
        }, {
          'header': 'Rating',
          'field': 'prompt_rating'
        }];
      case 'nps_with_user_feedback':
        return [{
          'header': 'Feedback',
          'field': 'prompt_input1'
        }, {
          'header': 'NPS rating',
          'field': 'prompt_nps'
        }];
      default:
        return [];
    }
  }

  private createDILdownloadRequest(requestData) {
    const endPoint = '/dashboard/ujmanalytics/downloadrequest/create';

    const endDate = moment().utc().add(1, 'day').startOf('day').valueOf(); // Next day's start time 00:00
    const startDate = moment(endDate).subtract(moment.duration('P' + requestData.duration)).valueOf(); // duration before end time.
    const data = {
      app_id: requestData.app_id,
      email_ids: requestData.email_ids,
      jhash: requestData.journey_hash,
      prompt_path: requestData.prompt.lidx,
      name_of_nudge: requestData.prompt.title,
      type_of_nudge: requestData.prompt.template,
      nudge_display_type: requestData.prompt_template,
      start_date: startDate,
      end_date: endDate,
      fields: this.getDILDownloadDataFields(requestData.prompt.template),   // prompt input field
      event_name: 'hansel_prompt_event',                                    // prompt close event
    };

    return this.http.post<any>(endPoint, data).pipe(
      map((res: Response) => {
        const body = res;
        return body || {};
      })
    );
  }

  requestDownloadReportOfFeedbackNudges(data) {
    const url = `/dashboard/ujmanalytics/smt/prompts/generate-report`;
    return this.http.post<any>(url, data).pipe(
      map((res: Response) => {
        const body = res;
        return body || {};
      })
    );
  }
}
